import React, { useCallback, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';

import { fetchGroups } from '../../axiosClient/authApi';
import { addDraft, getContractType } from '../../axiosClient/draftApi';
import ControlledTextField from '../../components/ControlledTextField';
import CustomChip from '../../components/CustomChip';
import CustomModal from '../../components/CustomModal';
import RISelectComponent from '../../components/SelectComponent';
import UploadDocComponent from '../../components/UploadDocComponent';
import { IOptions } from '../../interfaces/IOptions';

interface IProps {
  open: boolean;
  handleClose: VoidFunction;
}

const CreateDraft: React.FC<IProps> = ({ open, handleClose }) => {
  const methods = useForm();
  const { control, handleSubmit } = methods;

  const [uploadedFile, setUploadedFile] = useState<File[]>([]);

  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const { data: groupListData, isLoading: groupLoading } = useQuery({
    queryKey: ['GroupList'],
    queryFn: async () => {
      const response = await fetchGroups();
      return response.results;
    },
  });

  const { data: contractTypes, isLoading: contractLoading } = useQuery({
    queryKey: ['contract_types'],
    queryFn: getContractType,
    select: (response) => {
      return response.results;
    },
  });

  const { mutate, status } = useMutation({
    mutationKey: ['add_draft'],
    mutationFn: addDraft,
    onSuccess: () => {
      enqueueSnackbar('Draft added successfully!', {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      queryClient.invalidateQueries({
        queryKey: ['drafts'],
      });
      handleClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to add Draft!', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    },
  });

  const onSubmit = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => {
      const file = uploadedFile[0];

      const fileData = file?.name?.split('.');
      data.file_name = `${data.file_name}.${fileData[fileData.length - 1]}`;
      data.groups = `[${data.groups}]`;

      const blob = new Blob([file], { type: 'octet' });
      const newFile = new File([blob], data.file_name, {
        type: fileData[fileData.length - 1],
      });

      data.file = newFile;
      mutate(data);
    },
    [uploadedFile]
  );

  return (
    <CustomModal
      title="Add new Draft"
      open={open}
      maxWidth="md"
      handleClose={handleClose}
    >
      <FormProvider {...methods}>
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          mt={2}
          spacing={2}
          sx={{ width: '500px', padding: '30px 10px' }}
        >
          <ControlledTextField
            name="file_name"
            variant="outlined"
            label="Draft Name"
            fullWidth
          />
          <ControlledTextField
            name="counter_party_name"
            variant="outlined"
            label="Counter Party Name"
            fullWidth
          />
          <UploadDocComponent
            label="Upload draft"
            control={control}
            name="file"
            allowedFileTypes={['.docx']}
            files={uploadedFile}
            setFiles={setUploadedFile}
          />
          <RISelectComponent
            name="contract_type"
            control={control}
            label="Contract Type"
            options={(contractTypes as IOptions[]) || []}
            loading={contractLoading}
            renderCustomComponent={(value: IOptions | string | number) => (
              <CustomChip
                key={(value as IOptions)?.id}
                label={(value as IOptions)?.name}
              />
            )}
          />
          <RISelectComponent
            name="groups"
            control={control}
            label="Prescribing Department"
            options={groupListData || []}
            loading={groupLoading}
            isMultiselect={true}
            renderCustomComponent={(value: IOptions | string | number) => (
              <CustomChip
                key={(value as IOptions)?.id}
                label={(value as IOptions)?.name}
              />
            )}
          />
          <LoadingButton
            type="submit"
            variant="contained"
            loading={status === 'pending'}
            fullWidth
          >
            Add
          </LoadingButton>
        </Stack>
      </FormProvider>
    </CustomModal>
  );
};

export default CreateDraft;
